import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Sparkles } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || params.get("session_id");
  const failedFlag = params.get("failed") === "1" || (typeof window !== "undefined" && window.location.pathname.endsWith("/failed"));
  const navigate = useNavigate();
  const [status, setStatus] = useState(failedFlag ? "failed" : "polling");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!orderId) { setStatus("failed"); return; }
    if (failedFlag) return;
    let attempts = 0;
    const max = 8;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${orderId}`);
        setInfo(data);
        if (data.payment_status === "paid" || data.payment_status === "authorized") { setStatus("paid"); return; }
        if (data.status === "failed" || data.status === "signature_invalid") { setStatus("failed"); return; }
        attempts += 1;
        if (attempts >= max) { setStatus("timeout"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= max) { setStatus("failed"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [orderId, failedFlag]);

  return (
    <div className="min-h-screen bg-aurora text-zinc-900 flex flex-col">
      <header className="max-w-7xl mx-auto px-6 h-16 flex items-center w-full">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-8 w-8 bg-gradient-to-br from-indigo-500 to-sky-500 text-white rounded-md flex items-center justify-center"><Sparkles size={16} /></div>
          <span className="font-display font-black text-lg">SkillGap <span className="rainbow-text">Pilot</span></span>
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="card-flat p-10 max-w-md w-full text-center" data-testid="payment-success-card">
          {status === "polling" && (
            <>
              <Loader2 size={36} className="mx-auto animate-spin text-indigo-600" />
              <h1 className="font-display font-black text-2xl mt-4">Confirming your payment…</h1>
              <p className="text-sm text-zinc-600 mt-2">Razorpay is finalizing — this usually takes a few seconds.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 size={48} className="mx-auto text-emerald-600" />
              <h1 className="font-display font-black text-3xl mt-4">You&apos;re in!</h1>
              <p className="text-sm text-zinc-600 mt-2">Your <strong>{info?.plan_id?.replace(/_/g,' ')}</strong> plan is now <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Active</span> on SkillGap Pilot.</p>
              {info?.payment_status === "authorized" && (
                <p className="text-xs text-zinc-500 mt-2" data-testid="settlement-pending-note">
                  Bank settlement continues in the background. You already have full access.
                </p>
              )}
              <button data-testid="go-dashboard-btn" onClick={() => navigate("/app/dashboard")} className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-gradient-to-r from-indigo-500 to-sky-500 text-white font-semibold text-sm hover:opacity-90">
                Go to Dashboard <ArrowRight size={14} />
              </button>
            </>
          )}
          {(status === "failed" || status === "timeout") && (
            <>
              <XCircle size={48} className="mx-auto text-red-500" />
              <h1 className="font-display font-black text-2xl mt-4">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
              <p className="text-sm text-zinc-600 mt-2">{status === "timeout" ? "It's taking longer than usual. Check your email or refresh in a minute." : "We couldn't confirm your payment. Try again or contact support."}</p>
              <button data-testid="back-to-pricing-btn" onClick={() => navigate("/app/pricing")} className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-zinc-300 font-semibold text-sm hover:border-zinc-900">
                Back to pricing
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
